// --- Framework
import React from 'react';
import PropTypes from 'prop-types';

// --- External tools
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';

// --- IO
import API from 'io/API';

// --- Logic
import i18n from 'logic/translation/i18n';
import TranslationKey from 'logic/enums/TranslationKey';

// --- External components
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Select from '@material-ui/core/Select';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import { isValidEmailAddress } from '../../../logic/stringOperations';

// --- Style
const styles = theme => ({});


const formRules = Object.freeze({
	username: [
		value => !!value || { i18nKey: TranslationKey.validation_username_empty },
		// value => isValidEmailAddress(value) || { i18nKey: TranslationKey.validation_email_invalid },
	],
	password: [
		value => !!value || { i18nKey: TranslationKey.validation_password_empty },
	],
});

const defaultState = Object.freeze({
	username: '',
	password: '',
	errors: {
		username: [],
		password: [],
	},
	requestError: null,
	isFormDisabled: false,
});

class SignIn extends React.Component {
	constructor(props) {
		super(props);

		this.state = { ...defaultState };

		this.onSubmit = this.onSubmit.bind(this);
		this.validateForm = this.validateForm.bind(this);
		this.switchToRegister = this.switchToRegister.bind(this);
		this.switchToPasswordReset = this.switchToPasswordReset.bind(this);
		this.onLanguageSelected = this.onLanguageSelected.bind(this);
	}

	async validateForm() {
		let hasErrors = false;

		await this.setState((currentState) => {
			const errors = Object.keys(formRules).reduce((result, field) => {
				result[field] = formRules[field]
					.map(rule => rule(currentState[field]))
					.filter(ruleResult => ruleResult !== true);

				if (result[field].length > 0)
					hasErrors = true;

				return result;
			}, {});

			console.log('Form errors', errors);

			return { errors };
		});

		return hasErrors;
	}

	async onSubmit(event) {
		if (event != null)
			event.preventDefault();

		await this.setState(currentState => ({
			requestError: null,
			isFormDisabled: true,
			username: currentState.username.toLowerCase(),
			// email: currentState.email.toLowerCase(),
		}));

		const {
			props: {
				onSignedIn,
			},
			state: {
				username,
				password,
				// language
			}
		} = this;

		const hasErrors = await this.validateForm();

		// Invalid fields.
		if (hasErrors) {
			await this.setState({ isFormDisabled: false });
			return;
		}

		try {
			const response = await API.getUserFromCredentials(username, password);
			console.log('Sign-in response', response);

			// Wrong credentials.
			if (response.data == null || response.data.length <= 0) {
				await this.setState({
					isFormDisabled: false,
					requestError: { i18nKey: TranslationKey.error_sign_in_bad_credentials }
				});
				return;
			}

			const credentials = { username, password };
			const rawUserData = response.data;

			onSignedIn(credentials, rawUserData);
			this.setState({ isFormDisabled: false });
		} catch (error) {
			if (error.response && error.response.status === 400 && error.response.data && error.response.data.error) {
				await this.setState({
					isFormDisabled: false,
					requestError: { message: error.response.data.error }
				});
			} else {
				await this.setState({
					isFormDisabled: false,
					requestError: { i18nKey: TranslationKey.error_sign_in_bad_credentials }
				});
			}
		}
	}

	switchToRegister() {
		const { history } = this.props;

		history.push('/register');
	}

	switchToPasswordReset() {
		const { history } = this.props;

		history.push('/forgot-password');
	}

	switchLanguage(language) {
		// const { language } = this.props;
		// i18n.changeLanguage(language);
	}

	render() {
		const {
			props: {
				t,
				classes,
				supportedLanguages
			},
			state: {
				errors,
				requestError,
				isFormDisabled,
			}
		} = this;

		let languageSelector = null;
		languageSelector = (
			<Grid item>
				<FormControl
					fullWidth
					size="small"
					variant="outlined"
				>
					<InputLabel id="select-preferred-language-label">
						{t(TranslationKey.select_language)}
					</InputLabel>
					<Select
						value={i18n.language}
						id="select-preferred-language"
						onChange={this.onLanguageSelected}
						label={t(TranslationKey.select_language)}
						labelId="select-preferred-language-label"
					>
						{supportedLanguages.map(({ id, label }) => (
							<MenuItem
								key={id}
								value={label}
							>
								{t(label)}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>
		);

		return (
			<Container
				disableGutters
				className="page"
			>
				<Typography
					variant="h5"
					component="h2"
					className="pageTitle"
					style={{ textAlign: 'center', marginBottom: '16px' }}
				>
					{t(TranslationKey.sign_in)}
				</Typography>
				<Container
					noValidate
					maxWidth="xs"
					disableGutters
					component="form"
					onSubmit={this.onSubmit}
					className="pageContainer"
				>
					<Grid
						container
						spacing={2}
						justify="center"
						direction="column"
						alignItems="stretch"
						className="content"
					>
						<Grid item>
							<TextField
								fullWidth
								variant="outlined"
								disabled={isFormDisabled}
								value={this.state.username}
								label={t(TranslationKey.email)}
								error={errors.username.length > 0}
								helperText={errors.username.length > 0 ? t(errors.username[0].i18nKey, errors.username[0].values) : null}
								onChange={({ target: { value } }) => this.setState((previousState) => {
									if (errors.username.length > 0)
										return { username: value, errors: { ...previousState.errors, username: [] } };
									return { username: value };
								})}
							/>
						</Grid>
						<Grid item>
							<TextField
								fullWidth
								type="password"
								variant="outlined"
								id="current-password"
								disabled={isFormDisabled}
								value={this.state.password}
								autoComplete="current-password"
								error={errors.password.length > 0}
								label={t(TranslationKey.password)}
								helperText={errors.password.length > 0 ? t(errors.password[0].i18nKey, errors.password[0].values) : null}
								onChange={({ target: { value } }) => this.setState((previousState) => {
									if (errors.password.length > 0)
										return { password: value, errors: { ...previousState.errors, password: [] } };
									return { password: value };
								})}
							/>
						</Grid>

						{languageSelector}

						{requestError != null ? (
							<Grid item>
								<Typography variant="caption" color="error">
									{t(requestError.i18nKey, requestError.values)}
								</Typography>
							</Grid>
						) : null}
						<Grid item>
							<Button
								fullWidth
								size="large"
								type="submit"
								color="primary"
								disableElevation
								variant="contained"
								disabled={isFormDisabled}
							>
								{t(TranslationKey.sign_in)}
							</Button>
						</Grid>
						<Grid item>
							<Button
								fullWidth
								size="small"
								variant="text"
								color="primary"
								disabled={isFormDisabled}
								onClick={this.switchToRegister}
							>
								{t(TranslationKey.register)}
							</Button>
						</Grid>
						<Grid item>
							<Button
								fullWidth
								size="small"
								variant="text"
								color="primary"
								disabled={isFormDisabled}
								onClick={this.switchToPasswordReset}
							>
								{t(TranslationKey.forgot_password)}
							</Button>
						</Grid>

					</Grid>
				</Container>
			</Container>
		);
	}

	onLanguageSelected(event) {
		const { target: { value: selectedLanguage } } = event;

		const {
			switchLanguage,
			supportedLanguages,
		} = this.props;

		const languageToApply = supportedLanguages.find(({ label }) => label === selectedLanguage);

		if (languageToApply == null) {
			console.error(`The selected language ${selectedLanguage} is out of range, aborting.`);
			return;
		}

		switchLanguage(languageToApply);
	}
}

SignIn.propTypes = {
	t: PropTypes.func,
	switchLanguage: PropTypes.func,
	history: PropTypes.object.isRequired,
	classes: PropTypes.object.isRequired,
	onSignedIn: PropTypes.func.isRequired,
	supportedLanguages: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
	})),
};

SignIn.defaultProps = {
	switchLanguage: () => console.warn('switchLanguage method not provided.')
};

export default compose(
	withTranslation(),
	withStyles(styles),
)(SignIn);
